import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import {
  getEnduserCurrentProjectGroupCompletion,
  getEnduserProjectGroupsCompletion,
} from "../../features/enduserStats/state/enduserStatsSlice";
import { EmptyState } from "../../features/statsNew/components";

const EnduserGazeHeatmapView = () => {
  const { t } = useTranslation();
  const { parcoursId, moduleId, sessionId } = useParams<{
    parcoursId: string;
    moduleId: string;
    sessionId: string;
  }>();

  const projectGroupsCompletion = useSelector(getEnduserProjectGroupsCompletion);
  const currentProjectGroupCompletion = useSelector(getEnduserCurrentProjectGroupCompletion);

  // Find parcours info
  const parcoursInfo = useMemo(() => {
    return projectGroupsCompletion.find((pg) => pg.projectGroupId === Number(parcoursId));
  }, [projectGroupsCompletion, parcoursId]);

  // Find module info
  const moduleInfo = useMemo(() => {
    return currentProjectGroupCompletion.find((m) => m.projectId === Number(moduleId));
  }, [currentProjectGroupCompletion, moduleId]);

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb */}
      <nav className="stats-breadcrumb mb-2">
        <Link to="/stats/parcours">{t("enduserStats.tabs.parcours")}</Link>
        <span className="stats-breadcrumb-separator">/</span>
        <Link to={`/stats/parcours/${parcoursId}`}>{parcoursInfo?.projectGroupName}</Link>
        <span className="stats-breadcrumb-separator">/</span>
        <Link to={`/stats/parcours/${parcoursId}/${moduleId}`}>{moduleInfo?.projectName}</Link>
        <span className="stats-breadcrumb-separator">/</span>
        <Link to={`/stats/parcours/${parcoursId}/${moduleId}/session/${sessionId}`}>
          {t("statsNew.session.session")} #{Number(sessionId) + 1}
        </Link>
        <span className="stats-breadcrumb-separator">/</span>
        <span className="stats-breadcrumb-current">{t("enduserStats.heatmap.title")}</span>
      </nav>

      {/* Header */}
      <div className="flex-shrink-0 bg-white rounded-lg border border-gray-200 px-4 py-3 mb-3">
        <h2 className="text-lg font-bold text-gray-900">{t("enduserStats.heatmap.title")}</h2>
        <p className="text-sm text-gray-500">
          {t("statsNew.session.session")} #{Number(sessionId) + 1} - {moduleInfo?.projectName}
        </p>
      </div>

      {/* Placeholder for heatmap viewer */}
      <div className="flex-1 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <EmptyState
          icon="search"
          title={t("enduserStats.heatmap.noScenes")}
          description={t("enduserStats.heatmap.noScenesDescription")}
        />
      </div>

      {/* Back button */}
      <div className="flex-shrink-0 mt-3">
        <Link
          to={`/stats/parcours/${parcoursId}/${moduleId}/session/${sessionId}`}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          {t("enduserStats.backToSession")}
        </Link>
      </div>
    </div>
  );
};

export default EnduserGazeHeatmapView;
